import { DietaryRow } from '@fusion/dietary/row'
import { Add, AddShoppingCart, Remove } from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack as MuiStack,
  Typography,
  ButtonGroup,
  Button,
  CardActions,
  IconButton,
  CircularProgress,
} from '@mui/material'

import type React from 'react'
import { memo, lazy, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { MenuItem } from '../../domain/menu-item'
import type { MenuVendor } from '../../domain/menu-vendor'
import { useIsPopularItem } from '../../store/popularItemsStore'
import { getMenuItemTranslatedValue } from '../../utils/getMenuItemTranslatedValue'
import { CardImage } from './CardImage'
import { ItemPrice } from './item-price'
import { ServesLabel } from './serves-label'
import { useMenuItemCard } from './useMenuItemCard'
import { useMenuItemCardOptimized } from './useMenuItemCardOptimized'

const MenuItemModal = lazy(() =>
  import('./menu-item-modal').then((module) => ({ default: module.MenuItemModal })),
)

export type Props = {
  item: MenuItem
  vendor: MenuVendor
  taxAcronym: string
  numberInCol: number
}

const SupplierMenuItemCard: React.FC<Props> = ({ item, vendor, taxAcronym, numberInCol }) => {
  const { t, i18n } = useTranslation()
  const currency = vendor?.currency || 'GBP'
  const [modalIsOpen, toggleModalIsOpen] = useState(false)
  const {
    quantity,
    account,
    servesAmount,
    price,
    priceCaveat,
    quickAddDisabled,
    handleQuantityIncrease,
    handleQuantityDecrease,
    handleAddItemToBasket,
    handleAddToCart,
    minimumQuantity,
  } = useMenuItemCard(item, vendor)
  const isPopular = useIsPopularItem(item._id)

  const isCompact = numberInCol > 1
  const cardMinHeight = isCompact ? 180 : 280

  const locale = i18n.language?.split('-')[0]?.toLowerCase()
  const itemName = getMenuItemTranslatedValue({
    translations: item.translations,
    locale,
    key: 'name',
    fallback: item.name,
  })
  const itemShortDescription =
    getMenuItemTranslatedValue({
      translations: item.translations,
      locale,
      key: 'shortDescription',
      fallback: item.shortDescription,
    }) ||
    getMenuItemTranslatedValue({
      translations: item.translations,
      locale,
      key: 'description',
      fallback: item.description,
    })

  const shoppingCartHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (item.options && item.options.length > 0) {
      toggleModalIsOpen(true)
    } else {
      handleAddToCart(e)
    }
  }

  return (
    <>
      {modalIsOpen && (
        <Suspense
          fallback={
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1300,
              }}>
              <CircularProgress />
            </Box>
          }>
          <MenuItemModal
            initialQty={quantity}
            taxAcronym={taxAcronym}
            mode="add"
            menuItem={item}
            vendor={vendor}
            onConfirmClick={handleAddItemToBasket}
            onClose={() => toggleModalIsOpen(false)}
            accountName={account?.name}
          />
        </Suspense>
      )}

      <Card
        sx={{
          minHeight: `${cardMinHeight}px`,
          display: 'flex',
          flexDirection: 'row',
          position: 'relative',
          borderRadius: 2,
          boxShadow: 'none',
          border: (theme) => theme.borders.light,
          cursor: 'pointer',
          overflow: 'hidden',
          gap: 1,
          padding: { xs: 1, sm: 1, lg: 0.5 },
          flex: 1,

          '@media (max-width: 600px)': {
            flexDirection: 'column',
            height: 'auto',
            gap: 1,
          },
        }}
        onClick={() => toggleModalIsOpen(true)}>
        <CardImage
          item={item}
          account={account ? { name: account.name } : undefined}
          t={t}
          numberInCol={numberInCol}
          popular={isPopular}
        />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
          }}>
          <CardHeader
            title={itemName}
            sx={{
              px: { xs: 0 },
              py: isCompact ? 0.5 : 1,
              '& .MuiCardHeader-title': {
                fontSize: isCompact ? '0.875rem' : '1.25rem',
                fontWeight: 600,
              },
              '& .MuiCardHeader-content': {
                overflow: 'hidden',
              },
            }}
          />
          <CardContent
            sx={{
              paddingY: 0,
              flex: 1,
              px: { xs: 1, sm: 0 },
              wordBreak: 'break-word',
              '& .MuiTypography-body2': {
                display: '-webkit-box',
                WebkitLineClamp: isCompact ? 2 : 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              },
            }}>
            {itemShortDescription && (
              <Typography color="text.secondary" variant="body3">
                {itemShortDescription}
              </Typography>
            )}
          </CardContent>

          <MuiStack
            direction="row"
            alignItems="center"
            gap={1}
            px={{ xs: 0 }}
            my={isCompact ? 0.5 : 1}>
            {item.dietaries && item.dietaries.length > 0 && (
              <DietaryRow
                variant="compact"
                renderOverflow={({ count }) => (
                  <Typography variant="body3" textAlign="center">
                    +{count}
                  </Typography>
                )}
                dietaries={item.dietaries}
                allergens={item.allergens || []}
                maxToShow={4}
              />
            )}
            {servesAmount && <ServesLabel servesAmount={servesAmount} />}
          </MuiStack>

          <CardActions
            sx={{
              justifyContent: 'space-between',
              alignItems: 'end',
              px: { xs: 0, sm: 0 },
              pb: { xs: 0, sm: 1 },
              pr: { xs: 0, sm: 1 },
              mt: isCompact ? 0 : undefined,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <ButtonGroup
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 2,
                  '& .MuiButton-root': {
                    borderRadius: 0,
                  },
                  '& .MuiButton-root:first-of-type': {
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                  },
                  '& .MuiButton-root:last-of-type': {
                    borderTopRightRadius: 8,
                    borderBottomRightRadius: 8,
                  },
                }}>
                <Button
                  data-cy="item-decrease"
                  onClick={handleQuantityDecrease}
                  disabled={quantity <= 0 || quantity <= minimumQuantity}>
                  <Remove fontSize={isCompact ? 'small' : 'medium'} />
                </Button>
                <Button disabled={quantity <= 0} sx={{ minWidth: 40, px: 1 }}>
                  <Typography variant="subtitle3">{quantity}</Typography>
                </Button>
                <Button data-cy="item-increase" onClick={handleQuantityIncrease}>
                  <Add fontSize={isCompact ? 'small' : 'medium'} />
                </Button>
              </ButtonGroup>
              <IconButton
                data-cy="AddShoppingCartIcon"
                onClick={shoppingCartHandler}
                disabled={quickAddDisabled}
                color="primary"
                size={isCompact ? 'small' : 'medium'}>
                <AddShoppingCart />
              </IconButton>
            </Box>
            <ItemPrice
              currency={currency}
              price={price}
              priceCaveat={priceCaveat}
              taxAcronym={taxAcronym}
              compact={isCompact}
            />
          </CardActions>
        </Box>
      </Card>
    </>
  )
}

const MemoizedMenuItemCard = memo(SupplierMenuItemCard, (prevProps, nextProps) => {
  return (
    prevProps.item._id === nextProps.item._id &&
    prevProps.vendor.id === nextProps.vendor.id &&
    prevProps.taxAcronym === nextProps.taxAcronym &&
    prevProps.numberInCol === nextProps.numberInCol
  )
})

/**
 * Optimized version of MenuItemCard that uses shared context.
 * Must be used within MenuItemCardProvider.
 */
const SupplierMenuItemCardOptimized: React.FC<Props> = ({
  item,
  vendor,
  taxAcronym,
  numberInCol,
}) => {
  const { t, i18n } = useTranslation()
  const currency = vendor?.currency || 'GBP'
  const [modalIsOpen, toggleModalIsOpen] = useState(false)
  const {
    quantity,
    account,
    servesAmount,
    price,
    priceCaveat,
    quickAddDisabled,
    handleQuantityIncrease,
    handleQuantityDecrease,
    handleAddItemToBasket,
    handleAddToCart,
    minimumQuantity,
  } = useMenuItemCardOptimized(item, vendor)
  const isPopular = useIsPopularItem(item._id)

  const isCompact = numberInCol > 1
  const cardMinHeight = isCompact ? 180 : 280

  const locale = i18n.language?.split('-')[0]?.toLowerCase()
  const itemName = getMenuItemTranslatedValue({
    translations: item.translations,
    locale,
    key: 'name',
    fallback: item.name,
  })
  const itemShortDescription =
    getMenuItemTranslatedValue({
      translations: item.translations,
      locale,
      key: 'shortDescription',
      fallback: item.shortDescription,
    }) ||
    getMenuItemTranslatedValue({
      translations: item.translations,
      locale,
      key: 'description',
      fallback: item.description,
    })

  const shoppingCartHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (item.options && item.options.length > 0) {
      toggleModalIsOpen(true)
    } else {
      handleAddToCart(e)
    }
  }

  return (
    <>
      {modalIsOpen && (
        <Suspense
          fallback={
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1300,
              }}>
              <CircularProgress />
            </Box>
          }>
          <MenuItemModal
            initialQty={quantity}
            taxAcronym={taxAcronym}
            mode="add"
            menuItem={item}
            vendor={vendor}
            onConfirmClick={handleAddItemToBasket}
            onClose={() => toggleModalIsOpen(false)}
            accountName={account?.name}
          />
        </Suspense>
      )}

      <Card
        sx={{
          minHeight: `${cardMinHeight}px`,
          display: 'flex',
          flexDirection: 'row',
          position: 'relative',
          borderRadius: 2,
          boxShadow: 'none',
          border: (theme) => theme.borders.light,
          cursor: 'pointer',
          overflow: 'hidden',
          gap: 1,
          padding: { xs: 1, sm: 1, lg: 0.5 },
          flex: 1,

          '@media (max-width: 600px)': {
            flexDirection: 'column',
            height: 'auto',
            gap: 1,
          },
        }}
        onClick={() => toggleModalIsOpen(true)}>
        <CardImage
          item={item}
          account={account ? { name: account.name } : undefined}
          t={t}
          numberInCol={numberInCol}
          popular={isPopular}
        />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
          }}>
          <CardHeader
            title={itemName}
            sx={{
              px: { xs: 0 },
              py: isCompact ? 0.5 : 1,
              '& .MuiCardHeader-title': {
                fontSize: isCompact ? '0.875rem' : '1.25rem',
                fontWeight: 600,
              },
              '& .MuiCardHeader-content': {
                overflow: 'hidden',
              },
            }}
          />
          <CardContent
            sx={{
              paddingY: 0,
              flex: 1,
              px: { xs: 1, sm: 0 },
              wordBreak: 'break-word',
              '& .MuiTypography-body2': {
                display: '-webkit-box',
                WebkitLineClamp: isCompact ? 2 : 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              },
            }}>
            {itemShortDescription && (
              <Typography color="text.secondary" variant="body3">
                {itemShortDescription}
              </Typography>
            )}
          </CardContent>

          <MuiStack
            direction="row"
            alignItems="center"
            gap={1}
            px={{ xs: 0 }}
            my={isCompact ? 0.5 : 1}>
            {item.dietaries && item.dietaries.length > 0 && (
              <DietaryRow
                variant="compact"
                renderOverflow={({ count }) => (
                  <Typography variant="body3" textAlign="center">
                    +{count}
                  </Typography>
                )}
                dietaries={item.dietaries}
                allergens={item.allergens || []}
                maxToShow={4}
              />
            )}
            {servesAmount && <ServesLabel servesAmount={servesAmount} />}
          </MuiStack>

          <CardActions
            sx={{
              justifyContent: 'space-between',
              alignItems: 'end',
              px: { xs: 0, sm: 0 },
              pb: { xs: 0, sm: 1 },
              pr: { xs: 0, sm: 1 },
              mt: isCompact ? 0 : undefined,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <ButtonGroup
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 2,
                  '& .MuiButton-root': {
                    borderRadius: 0,
                  },
                  '& .MuiButton-root:first-of-type': {
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                  },
                  '& .MuiButton-root:last-of-type': {
                    borderTopRightRadius: 8,
                    borderBottomRightRadius: 8,
                  },
                }}>
                <Button
                  data-cy="item-decrease"
                  onClick={handleQuantityDecrease}
                  disabled={quantity <= 0 || quantity <= minimumQuantity}>
                  <Remove fontSize={isCompact ? 'small' : 'medium'} />
                </Button>
                <Button disabled={quantity <= 0} sx={{ minWidth: 40, px: 1 }}>
                  <Typography variant="subtitle3">{quantity}</Typography>
                </Button>
                <Button data-cy="item-increase" onClick={handleQuantityIncrease}>
                  <Add fontSize={isCompact ? 'small' : 'medium'} />
                </Button>
              </ButtonGroup>
              <IconButton
                data-cy="AddShoppingCartIcon"
                onClick={shoppingCartHandler}
                disabled={quickAddDisabled}
                color="primary"
                size={isCompact ? 'small' : 'medium'}>
                <AddShoppingCart />
              </IconButton>
            </Box>
            <ItemPrice
              currency={currency}
              price={price}
              priceCaveat={priceCaveat}
              taxAcronym={taxAcronym}
              compact={isCompact}
            />
          </CardActions>
        </Box>
      </Card>
    </>
  )
}

const MemoizedMenuItemCardOptimized = memo(
  SupplierMenuItemCardOptimized,
  (prevProps, nextProps) => {
    return (
      prevProps.item._id === nextProps.item._id &&
      prevProps.vendor.id === nextProps.vendor.id &&
      prevProps.taxAcronym === nextProps.taxAcronym &&
      prevProps.numberInCol === nextProps.numberInCol
    )
  },
)

MemoizedMenuItemCardOptimized.displayName = 'MemoizedMenuItemCardOptimized'

export default MemoizedMenuItemCard
export { SupplierMenuItemCard, MemoizedMenuItemCard, MemoizedMenuItemCardOptimized }
export { MenuItemCardProvider } from './MenuItemCardContext'
